import { Box, Flex, Progress, Text } from '@chakra-ui/react';
import { passwordStrength } from 'check-password-strength';
import { useEffect } from 'react';

const Icon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
            d="M13.3337 4L6.00033 11.3333L2.66699 8"
            stroke="#4318FF"
            strokeWidth="1.33"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);
const Dot = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="2" fill="#171923" />
    </svg>
);

const Item = ({ isTrue, text }: { isTrue: boolean; text: string }) => (
    <Flex gap="8px">
        {isTrue ? <Icon /> : <Dot />}
        <Text color="gray.900" fontSize="14px">
            {text}
        </Text>
    </Flex>
);

const strong = 'Strong';
const weak = 'Weak';
const tooWeak = 'Too weak';

const getValue = (value: string) => {
    if (value === tooWeak)
        return {
            color: 'red',
            progress: 33.3,
        };
    if (value === weak)
        return {
            color: 'orange',
            progress: 66.6,
        };
    if (value === strong)
        return {
            color: 'green',
            progress: 100,
        };
    return {
        color: 'red',
        progress: 0,
    };
};

type TProps = {
    password: string;
    setDisabled?: (value: boolean) => void;
};

const PasswordStrength = ({ password, setDisabled }: TProps) => {
    const { contains, length, value } = passwordStrength(password);
    const isLength = length >= 12;
    const isLower = contains.includes('lowercase');
    const isUpper = contains.includes('uppercase');
    const isNumber = contains.includes('number');
    const isSymbol = contains.includes('symbol');
    const { color, progress } = getValue(value);

    const init = password.length === 0;
    const disabled = isLength && isLower && isUpper && isNumber && isSymbol;

    useEffect(() => {
        setDisabled && setDisabled(!disabled);
    }, [password]); // eslint-disable-line

    return (
        <Box>
            <Flex justifyContent="space-between">
                <Text color="gray.900" fontSize="14px" fontWeight="500">
                    Password strength
                </Text>
                <Text color="gray.900" fontSize="14px" fontWeight="700">
                    {value}
                </Text>
            </Flex>
            <Progress
                className="my-progress"
                colorScheme={color}
                height="10px"
                value={init ? 0 : progress}
                width="100%"
                mt="8px"
                mb="24px"
            />
            <Flex flexDirection="column" gap="8px" mb="24px">
                <Item text="At least 12 characters" isTrue={isLength} />
                <Item text="One lower case letter" isTrue={isLower} />
                <Item text="One upper case letter" isTrue={isUpper} />
                <Item text="One number" isTrue={isNumber} />
                <Item text="One symbol" isTrue={isSymbol} />
            </Flex>
        </Box>
    );
};

export default PasswordStrength;
